import { criarConfiguracaoFormulario, GRUPOS_FORMULARIO } from './formularioConfiguracao';

/* Identificador do programa VETERANO no back-end. */
export const PROGRAMA_VETERANO = 2;

export const STEP_IDS = Object.freeze({
    DADOS_PESSOAIS: 'dadosPessoais',
    SOCIOECONOMICO: 'socioeconomico',
    DADOS_INSCRICAO: 'dadosInscricao',
    DOCUMENTOS: 'documentos',
    FINALIZAR: 'finalizar'
});

/* Etapas macro do cadastro, na ordem em que aparecem no Stepper do desktop. */
export const STEPS = Object.freeze([
    { id: STEP_IDS.DADOS_PESSOAIS, label: 'Dados Pessoais' },
    { id: STEP_IDS.SOCIOECONOMICO, label: 'Socioeconômico' },
    { id: STEP_IDS.DADOS_INSCRICAO, label: 'Dados da Inscrição' },
    { id: STEP_IDS.DOCUMENTOS, label: 'Documentos' },
    { id: STEP_IDS.FINALIZAR, label: 'Finalizar Inscrição' }
]);

export const STEP_LABELS = STEPS.map(step => step.label);

/*
 * Idade em anos a partir de uma data em dd/MM/yyyy, ISO ou Date.
 * Versão tolerante usada tanto pela configuração de seções quanto pelos formulários.
 */
export const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return 0;

    let parsedDate;
    if (typeof dataNascimento === 'string' && dataNascimento.includes('/')) {
        const parts = dataNascimento.split('/');
        if (parts.length === 3) {
            parsedDate = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
        }
    } else {
        parsedDate = new Date(dataNascimento);
    }

    if (!parsedDate || isNaN(parsedDate.getTime())) return 0;

    const today = new Date();
    let age = today.getFullYear() - parsedDate.getFullYear();
    const m = today.getMonth() - parsedDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < parsedDate.getDate())) {
        age--;
    }
    return age;
};

/*
 * Definição das seções de cada etapa macro.
 *
 * id      -> identificador estável, usado como id da tab (desktop) e da etapa (mobile)
 * label   -> título exibido na tab e na navegação mobile
 * fields  -> chaves de primeiro nível dos valores do formulário que pertencem à seção. Usadas para
 *            calcular `hasError` e para validar somente a seção atual no mobile.
 * visible -> (opcional) predicado sobre o contexto { config, formulario, inscricao }
 */
const SECOES_POR_ETAPA = {
    [STEP_IDS.DADOS_PESSOAIS]: [
        {
            id: 'dadosBasicos',
            label: 'Dados Pessoais',
            fields: ['pessoa']
        },
        {
            id: 'endereco',
            label: 'Endereço Residencial',
            fields: ['endereco']
        },
        {
            id: 'responsavelLegal',
            label: 'Responsável Legal',
            fields: ['tipoResponsavelLegal', 'dataEmancipacao', 'responsavelLegal'],
            visible: ({ inscricao }) => {
                const idade = calcularIdade(inscricao?.pessoa?.dataNascimento);
                return idade > 0 && idade < 18;
            }
        }
    ],
    [STEP_IDS.SOCIOECONOMICO]: [
        {
            id: 'declaracaoMoradia',
            label: 'Declaração e Moradia',
            fields: ['declaracaoIR', 'cadUnicoOuTransferenciaRenda', 'tipoHabitacao', 'tipoMoradia'],
            resumoErros: {
                campos: {
                    cadUnicoOuTransferenciaRenda: 'Participa do CadÚnico ou de programas de transferência de renda'
                }
            },
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.RENDA)
                || config.isVisible(GRUPOS_FORMULARIO.MORADIA)
        },
        {
            id: 'rendas',
            label: 'Minhas Rendas',
            fields: ['rendas', 'vlrPensao', 'vlrPensaoFilho', 'disponibilidade'],
            resumoErros: {
                campos: {
                    rendas: 'Fontes de renda',
                    vlrPensao: 'Pensão alimentícia',
                    vlrPensaoFilho: 'Pensão alimentícia dos filhos',
                    disponibilidade: 'Valor disponível para pagamento do curso'
                },
                linhas: {
                    rendas: (renda, indice) => renda?.ocupacao || `Renda ${indice + 1}`
                }
            },
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.RENDA)
                || config.isVisible(GRUPOS_FORMULARIO.OUTROS_TIPOS_DE_RENDA)
                || config.isVisible(GRUPOS_FORMULARIO.VALOR_DISPONIVEL)
        },
        {
            id: 'grupoFamiliar',
            label: 'Grupo Familiar',
            fields: ['composicaoFamiliar', 'enderecoComposicaoFamiliares'],
            resumoErros: {
                campos: {
                    composicaoFamiliar: 'Composição do grupo familiar',
                    enderecoComposicaoFamiliares: 'Endereços do grupo familiar'
                },
                linhas: {
                    composicaoFamiliar: (membro, indice) => membro?.descricao || `Membro ${indice + 1}`,
                    enderecoComposicaoFamiliares: (endereco, indice) => endereco?.logradouro || `Endereço ${indice + 1}`
                }
            },
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.GRUPO_FAMILIAR)
        },
        {
            id: 'parentes',
            label: 'Parentes UNAERP',
            fields: ['parentesBeneficiarios'],
            resumoErros: {
                campos: { parentesBeneficiarios: 'Parentes beneficiários' },
                linhas: {
                    parentesBeneficiarios: (parente, indice) => parente?.nome || `Parente ${indice + 1}`
                }
            },
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.PARENTE_BENEFICIARIO)
        }
    ],
    [STEP_IDS.DADOS_INSCRICAO]: [
        {
            id: 'opcoes',
            label: 'Opções de Inscrição',
            fields: ['vaga', 'sistemaCotas'],
            resumoErros: {
                campos: { sistemaCotas: 'Sistema de cotas' }
            }
        },
        {
            id: 'processoCategoria',
            label: 'Processo e Categoria',
            fields: ['itemSelecionadoPsInscricao', 'categoriaSelecionadoPsInscricao'],
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.INSCRICAO_PROCESSO)
                || config.isVisible(GRUPOS_FORMULARIO.CATEGORIA)
        },
        {
            id: 'veterano',
            label: 'Dados de Veterano',
            fields: ['inscricaoVeterano'],
            resumoErros: {
                campos: { inscricaoVeterano: 'Dados de veterano' }
            },
            visible: ({ config, formulario }) => formulario.programaId === PROGRAMA_VETERANO && (
                config.isVisible(GRUPOS_FORMULARIO.POSSUI_ENEM_VETERANO)
                || config.isVisible(GRUPOS_FORMULARIO.POSSUI_BENEFICIO_VETERANO)
                || config.isVisible(GRUPOS_FORMULARIO.PARTICIPOU_PROCESSO_VETERANO)
            )
        },
        {
            id: 'escolaridade',
            label: 'Escolaridade',
            fields: ['inscricaoEnsino'],
            resumoErros: {
                campos: { inscricaoEnsino: 'Escolaridade' },
                linhas: {
                    inscricaoEnsino: (ensino, indice) => ensino?.dsCurso
                        || ensino?.formacao?.descricao
                        || `Formação ${indice + 1}`
                }
            },
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.ESCOLARIDADE)
        },
        {
            id: 'enem',
            label: 'Pontuação do ENEM',
            fields: ['inscricaoEnem'],
            /* Os demais campos do ENEM já exibem a mensagem no próprio campo. */
            resumoErros: {
                campos: { 'inscricaoEnem.nomeArquivo': 'Comprovante do ENEM' }
            },
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.GRUPO_NOTA_ENEM)
        },
        {
            id: 'motivacao',
            label: 'Motivação',
            fields: ['motivo'],
            visible: ({ config }) => config.isVisible(GRUPOS_FORMULARIO.MOTIVO)
        }
    ],
    /* Documentos é uma etapa única: as abas por integrante ficam dentro dela. */
    [STEP_IDS.DOCUMENTOS]: [
        {
            id: 'documentos',
            label: 'Documentos',
            fields: ['inscricaoAnexos']
        }
    ],
    [STEP_IDS.FINALIZAR]: [
        {
            id: 'finalizar',
            label: 'Finalizar Inscrição',
            fields: ['aceitarTermos']
        }
    ]
};

const criarContexto = ({ formulario = {}, inscricao = {} } = {}) => ({
    config: criarConfiguracaoFormulario(formulario.configuracoesFormulario),
    formulario,
    inscricao
});

/* Seções visíveis de uma etapa macro, na ordem de exibição. */
export function getStepSections(stepId, ctx) {
    const secoes = SECOES_POR_ETAPA[stepId] || [];
    const contexto = criarContexto(ctx);

    return secoes
        .filter(secao => !secao.visible || secao.visible(contexto))
        .map(({ visible, ...secao }) => secao);
}

/*
 * Lista plana de etapas do mobile: cada seção visível de cada etapa macro vira uma etapa.
 * Em modo edição, `restrictToStepIndex` limita a navegação às seções da etapa macro escolhida.
 */
export function buildMobileSteps(ctx, { restrictToStepIndex = null } = {}) {
    const mobileSteps = [];

    STEPS.forEach((step, stepIndex) => {
        if (restrictToStepIndex !== null && stepIndex !== restrictToStepIndex) {
            return;
        }

        getStepSections(step.id, ctx).forEach(secao => {
            mobileSteps.push({
                flatIndex: mobileSteps.length,
                stepIndex,
                stepId: step.id,
                groupLabel: step.label,
                sectionId: secao.id,
                label: secao.label,
                fields: secao.fields,
                resumoErros: secao.resumoErros
            });
        });
    });

    return mobileSteps;
}

/* Erros (mensagens aninhadas, ver validacaoYup.js) que pertencem à seção. */
export function pickSectionErrors(section, errors = {}) {
    return (section?.fields || []).reduce((acc, field) => {
        if (errors[field] !== undefined && errors[field] !== null) {
            acc[field] = errors[field];
        }
        return acc;
    }, {});
}

/* Lê `alvo` por caminho: 'rendas' ou 'inscricaoEnem.nomeArquivo'. */
const obterEmCaminho = (alvo, caminho) => caminho
    .split('.')
    .reduce((atual, parte) => (atual == null ? undefined : atual[parte]), alvo);

/* Achata um erro (string, objeto ou array aninhado) na lista de mensagens. */
const coletarMensagens = (erro) => {
    if (!erro) return [];
    if (typeof erro === 'string') return [erro];
    if (Array.isArray(erro)) return erro.flatMap(coletarMensagens);
    if (typeof erro === 'object') return Object.values(erro).flatMap(coletarMensagens);
    return [];
};

/*
 * Mensagens de erro da seção prontas para exibição, identificando o registro
 * problemático nas seções baseadas em lista (rendas, grupo familiar, parentes).
 */
export function listarErrosSecao(section, errors = {}, values = {}, apenasCampos = null) {
    if (!section?.resumoErros) return [];

    const rotulos = section.resumoErros.campos || {};
    const rotuloLinha = section.resumoErros.linhas || {};
    const mensagens = [];
    const filtro = apenasCampos?.length ? apenasCampos : null;

    Object.keys(rotulos).filter(field => !filtro || filtro.includes(field)).forEach(field => {
        const erro = obterEmCaminho(errors, field);
        if (!erro) return;

        if (Array.isArray(erro)) {
            erro.forEach((erroLinha, indice) => {
                const detalhes = coletarMensagens(erroLinha);
                if (!detalhes.length) return;

                const nome = rotuloLinha[field]
                    ? rotuloLinha[field](obterEmCaminho(values, field)?.[indice], indice)
                    : `Item ${indice + 1}`;
                mensagens.push(`${nome}: ${detalhes.join(', ')}`);
            });
            return;
        }

        coletarMensagens(erro).forEach(detalhe => {
            mensagens.push(rotulos[field] ? `${rotulos[field]}: ${detalhe}` : detalhe);
        });
    });

    return mensagens;
}

/*
 * Texto do aviso ao tentar avançar com pendências: detalha o que falta quando a
 * seção sabe descrever seus erros e, caso contrário, ao menos nomeia as seções.
 */
export function mensagemErroEtapa(secoesComErro = [], errors = {}, values = {}, limite = 3) {
    const detalhes = secoesComErro.flatMap(secao => listarErrosSecao(secao, errors, values));

    if (detalhes.length) {
        const exibidos = detalhes.slice(0, limite).join(' · ');
        const restantes = detalhes.length - limite;
        return restantes > 0 ? `Corrija: ${exibidos} (e mais ${restantes})` : `Corrija: ${exibidos}`;
    }

    if (secoesComErro.length) {
        return `Corrija os erros em: ${secoesComErro.map(secao => secao.label).join(', ')}`;
    }

    return 'Corrija os erros marcados antes de prosseguir';
}

/* Há erro em algum dos campos informados? Usado para pintar só o bloco afetado. */
export function temErroNosCampos(errors = {}, campos = []) {
    return campos.some(campo => Boolean(obterEmCaminho(errors, campo)));
}

export function sectionHasError(section, errors = {}) {
    return Object.keys(pickSectionErrors(section, errors)).length > 0;
}

/*
 * Junta as seções visíveis da etapa com o JSX de cada uma.
 * `entries` é a lista { id, content } declarada no componente da etapa; entradas
 * sem seção correspondente (ou seções sem conteúdo) são descartadas.
 */
/* `form` é o retorno de useFormularioInscricao (submitCount + erros como mensagens). */
export function buildTabContent(sections, entries, form) {
    const conteudoPorId = new Map((entries || []).filter(Boolean).map(entry => [entry.id, entry]));

    return sections
        .map(section => {
            const entry = conteudoPorId.get(section.id);
            if (!entry) return null;

            return {
                id: section.id,
                label: entry.label || section.label,
                content: entry.content,
                summary: entry.summary,
                disabled: entry.disabled,
                hasError: form.submitCount > 0 && sectionHasError(section, form.erros)
            };
        })
        .filter(Boolean);
}
